import { useCallback } from 'react';
import { useDataProvider, useGetIdentity } from 'react-admin';

// 1. Define the schema type for Crop Classes data
export type CropClassImportSchema = {
    crop: string;
    class: string;
    state: string;
    aip: string;
};

// 2. Normalization function for Crop Classes spreadsheet
// Assumes headers: Crop | Class | State | AIP
export function normalizeCropClasses(
    data: Record<string, any>[]
): CropClassImportSchema[] {
    console.log(`Normalizing ${data.length} crop class records...`);
    const normalized: CropClassImportSchema[] = [];

    const dataArray = Array.isArray(data) ? data : [data];
    if (dataArray.length === 0) return [];

    for (const row of dataArray) {
        // Get value based on assumed header name OR column letter if headers are wrong/missing
        const getStr = (
            headerOrColA: string,
            headerOrColB?: string,
            headerOrColC?: string,
            headerOrColD?: string,
            headerOrColE?: string,
            defaultValue = ''
        ) => {
            if (row && typeof row === 'object') {
                // Prioritize explicit header names
                if (row[headerOrColA] != null)
                    return String(row[headerOrColA]).trim();
                if (headerOrColB && row[headerOrColB] != null)
                    return String(row[headerOrColB]).trim();
                if (headerOrColC && row[headerOrColC] != null)
                    return String(row[headerOrColC]).trim();
                if (headerOrColD && row[headerOrColD] != null)
                    return String(row[headerOrColD]).trim();
                if (headerOrColE && row[headerOrColE] != null)
                    return String(row[headerOrColE]).trim();
                // Fallback to column position (A=0, B=1, etc.) if headers don't match
                const keys = Object.keys(row);
                const colIndexMap: { [key: string]: number } = {
                    A: 0,
                    B: 1,
                    C: 2,
                    D: 3,
                    E: 4,
                }; // Note: Indices C, D used below now map to State, AIP
                const colIndex = colIndexMap[headerOrColA];
                if (
                    colIndex !== undefined &&
                    keys[colIndex] !== undefined &&
                    row[keys[colIndex]] != null
                ) {
                    console.warn(
                        `Using column position ${headerOrColA} for header ${headerOrColA} as header was not found explicitly.`
                    );
                    return String(row[keys[colIndex]]).trim();
                }
            }
            return defaultValue;
        };

        normalized.push({
            crop: getStr('Crop', undefined, undefined, undefined, 'A'), // Column A fallback
            class: getStr('Class', undefined, undefined, undefined, 'B'), // Column B fallback
            state: getStr('State', undefined, undefined, undefined, 'C'), // Column C fallback
            aip: getStr('AIP', undefined, undefined, undefined, 'D'), // Column D fallback
        });
    }

    console.log(
        'Normalization complete for crop classes.',
        normalized.slice(0, 5)
    ); // Log first 5 for verification
    return normalized;
}

// 3. Import hook for Crop Classes
export function useCropClassesImport(selectedSpreadsheet: string) {
    const { identity } = useGetIdentity();
    const dataProvider = useDataProvider();

    const processBatch = useCallback(
        async (batch: CropClassImportSchema[]) => {
            const userId = identity?.id;

            if (userId === undefined || userId === null) {
                console.error(
                    'useCropClassesImport: Missing user ID from identity. Value:',
                    userId
                );
                throw new Error(
                    'User identity not available for record creation.'
                );
            }

            let processedCount = 0;
            for (const item of batch) {
                try {
                    await dataProvider.create('crop_classes', {
                        data: {
                            crop: item.crop,
                            class: item.class,
                            state: item.state,
                            aip: item.aip,
                            created_at: new Date().toISOString(),
                            spreadsheet_type: selectedSpreadsheet,
                        },
                        meta: { user: identity },
                    });
                    processedCount++;
                } catch (error) {
                    console.error(
                        'useCropClassesImport: Failed to create crop class record:',
                        item,
                        'Error:',
                        error
                    );
                    throw error; // Re-throw to signal failure
                }
            }
            console.log(
                `useCropClassesImport: Successfully processed ${processedCount} records sequentially in this batch.`
            );
        },
        [dataProvider, selectedSpreadsheet, identity]
    );

    return processBatch;
}
